/**
 * Rehab tab — editable line items, sum auto-populates parent's `rehab` total.
 *
 * Default items (interior paint, kitchen refresh, etc.) come from the parent;
 * user can edit any amount, add ad-hoc items, or delete custom items.
 */
import { Plus, Trash2, Wrench } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { cn } from "@/lib/utils";
import { fmtMoney, round2 } from "@/lib/calculators";
import type { RehabSettings } from "@/types/pipeline";

interface RehabBreakdownProps {
  lineItems: NonNullable<RehabSettings["line_items"]>;
  customItems: NonNullable<RehabSettings["custom_items"]>;
  onLineItemsChange: (items: NonNullable<RehabSettings["line_items"]>) => void;
  onCustomItemsChange: (
    items: NonNullable<RehabSettings["custom_items"]>,
  ) => void;
}

export function RehabBreakdown({
  lineItems,
  customItems,
  onLineItemsChange,
  onCustomItemsChange,
}: RehabBreakdownProps) {
  const total =
    lineItems.reduce((s, i) => s + round2(i.amount || 0), 0) +
    customItems.reduce((s, i) => s + round2(i.amount || 0), 0);

  function updateLineItemAmount(idx: number, amount: number) {
    const next = [...lineItems];
    next[idx] = { ...next[idx], amount };
    onLineItemsChange(next);
  }
  function updateCustomItem(idx: number, patch: Partial<{ label: string; amount: number }>) {
    const next = [...customItems];
    next[idx] = { ...next[idx], ...patch };
    onCustomItemsChange(next);
  }
  function deleteCustom(idx: number) {
    onCustomItemsChange(customItems.filter((_, i) => i !== idx));
  }
  function addCustom() {
    onCustomItemsChange([...customItems, { label: "", amount: 0 }]);
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <Wrench className="h-4 w-4 text-primary" aria-hidden />
        Estimated Rehab Scope
        <span className="ml-auto text-xs font-medium text-muted-foreground">
          Editable line items
        </span>
      </h3>

      <div className="space-y-1.5 text-sm">
        {lineItems.map((item, idx) => (
          <RehabRow
            key={item.id ?? `std-new-${idx}`}
            label={item.label}
            amount={round2(item.amount)}
            onAmountChange={(v) => updateLineItemAmount(idx, v)}
          />
        ))}

        {customItems.map((item, idx) => (
          <RehabRow
            key={item.id ?? `custom-new-${idx}`}
            customLabel={{
              value: item.label,
              onChange: (v) => updateCustomItem(idx, { label: v }),
            }}
            amount={round2(item.amount)}
            onAmountChange={(v) => updateCustomItem(idx, { amount: v })}
            onDelete={() => deleteCustom(idx)}
          />
        ))}

        <div className="pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCustom}
            className="border-dashed text-xs"
          >
            <Plus className="h-3 w-3" aria-hidden /> Add line item
          </Button>
        </div>

        <div className="flex items-center justify-between border-t border-border pt-3 text-base font-extrabold">
          <span>Total Rehab Estimate</span>
          <span>{fmtMoney(total)}</span>
        </div>
      </div>
    </div>
  );
}

interface RehabRowProps {
  label?: string;
  customLabel?: { value: string; onChange: (v: string) => void };
  amount: number;
  onAmountChange: (v: number) => void;
  onDelete?: () => void;
}

function RehabRow({
  label,
  customLabel,
  amount,
  onAmountChange,
  onDelete,
}: RehabRowProps) {
  return (
    <div className="flex items-center gap-2 border-b border-dashed border-border py-1.5">
      {customLabel ? (
        <Input
          value={customLabel.value}
          onChange={(e) => customLabel.onChange(e.target.value)}
          placeholder="Line item name"
          className="h-7 flex-1 border-0 bg-transparent px-1 text-sm placeholder:italic focus-visible:bg-muted/40 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      ) : (
        <span className="flex-1 text-sm text-muted-foreground">{label}</span>
      )}
      <div
        className={cn(
          "flex w-32 items-center rounded-md border border-border bg-muted/40 focus-within:border-primary",
        )}
      >
        <span className="pl-2 text-xs text-muted-foreground">$</span>
        <NumberInput
          value={amount}
          onChange={onAmountChange}
          className="h-7 border-0 bg-transparent pl-1 pr-2 text-right font-bold focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </div>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="grid h-6 w-6 place-items-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          aria-label="Remove line item"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
    </div>
  );
}
